import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type {
  HubspotPartnerFieldMapCreateInput,
  HubspotPartnerFieldMapRefreshResult,
  HubspotPartnerFieldMapRow,
  HubspotPartnerFieldMapUpdateInput,
} from "@colab/shared";

const ROUTE = "/settings/integrations/hubspot/partner-field-map";

function invalidate(client: ReturnType<typeof useQueryClient>) {
  client.invalidateQueries({ queryKey: queryKeys.settings.hubspotPartnerFieldMap() });
  client.invalidateQueries({
    queryKey: queryKeys.settings.hubspotDiscoverablePartnerProperties(),
  });
}

export function useCreatePartnerFieldMap() {
  const client = useQueryClient();
  return useMutation<
    HubspotPartnerFieldMapRow,
    ApiClientError,
    HubspotPartnerFieldMapCreateInput
  >({
    mutationFn: (input) =>
      apiFetch<HubspotPartnerFieldMapRow>(ROUTE, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => invalidate(client),
    onError: (err) => toast.error(err.message ?? "Failed to add field"),
  });
}

export function useUpdatePartnerFieldMap() {
  const client = useQueryClient();
  return useMutation<
    { ok: true },
    ApiClientError,
    { id: string; input: HubspotPartnerFieldMapUpdateInput }
  >({
    mutationFn: ({ id, input }) =>
      apiFetch<{ ok: true }>(`${ROUTE}/${id}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      }),
    onSuccess: () => invalidate(client),
    onError: (err) => toast.error(err.message ?? "Failed to update field"),
  });
}

export function useDeletePartnerFieldMap() {
  const client = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, string>({
    mutationFn: (id) =>
      apiFetch<{ ok: true }>(`${ROUTE}/${id}`, { method: "DELETE" }),
    onSuccess: () => invalidate(client),
    onError: (err) => toast.error(err.message ?? "Failed to remove field"),
  });
}

export function useRefreshPartnerFieldMapSchema() {
  const client = useQueryClient();
  return useMutation<HubspotPartnerFieldMapRefreshResult, ApiClientError, void>({
    mutationFn: () =>
      apiFetch<HubspotPartnerFieldMapRefreshResult>(
        `${ROUTE}/refresh-schema`,
        { method: "POST" },
      ),
    onSuccess: (result) => {
      invalidate(client);
      toast.success(
        result.stale === 0
          ? `Refreshed ${result.refreshed} field${result.refreshed === 1 ? "" : "s"}.`
          : `Refreshed ${result.refreshed} field${result.refreshed === 1 ? "" : "s"}. ${result.stale} no longer in HubSpot.`,
      );
    },
    onError: (err) => toast.error(err.message ?? "Failed to refresh schema"),
  });
}
